import React from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

export const Cart = () => {
  const { cartItems, cartTotal, loading, updateQuantity, removeFromCart } = useCart();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div data-testid="cart-empty" className="min-h-screen pt-[132px] pb-16 px-4 md:px-8 flex flex-col items-center justify-center">
        <div className="w-24 h-24 bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6">
          <ShoppingBag className="w-12 h-12 text-zinc-600" />
        </div>
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-2">Your Cart is Empty</h1>
        <p className="text-zinc-400 mb-8">Looks like you haven't added anything yet.</p>
        <Link to="/products">
          <Button 
            data-testid="continue-shopping-btn"
            className="h-14 px-8 bg-teal-400 text-black hover:bg-white font-heading font-black uppercase tracking-widest skew-x-[-10deg] group"
          >
            <span className="skew-x-[10deg] flex items-center gap-2">
              Continue Shopping
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen pt-[132px] pb-16 px-4 md:px-8">
      <div className="max-w-[1400px] mx-auto">
        <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter mb-12">
          Your <span className="gradient-text">Cart</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div 
                key={item.id}
                data-testid={`cart-item-${item.product_id}`}
                className="glass-panel p-4 md:p-6 flex flex-col sm:flex-row gap-4 md:gap-6"
              >
                {/* Image */}
                <Link to={`/products/${item.product_id}`} className="w-full sm:w-32 h-32 bg-zinc-900 flex-shrink-0">
                  <img 
                    src={item.product?.image} 
                    alt={item.product?.name}
                    className="w-full h-full object-cover"
                  />
                </Link>

                {/* Details */}
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <Link 
                      to={`/products/${item.product_id}`}
                      className="font-heading text-xl font-bold uppercase tracking-tight hover:text-teal-400 transition-colors"
                    >
                      {item.product?.name}
                    </Link>
                    <p className="text-zinc-400 text-sm mt-1 line-clamp-2">
                      {item.product?.short_description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    {/* Quantity */}
                    <div className="flex items-center border border-zinc-700">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        data-testid={`cart-decrease-${item.product_id}`}
                        className="p-2 hover:bg-zinc-800 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-10 text-center font-heading font-bold">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        data-testid={`cart-increase-${item.product_id}`}
                        className="p-2 hover:bg-zinc-800 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Price & Remove */}
                    <div className="flex items-center gap-4">
                      <span className="font-heading text-xl font-bold">
                        ₹{((item.product?.price || 0) * item.quantity).toLocaleString()}
                      </span>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        data-testid={`cart-remove-${item.product_id}`}
                        className="p-2 text-zinc-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="glass-panel p-6 md:p-8 sticky top-28">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-zinc-400">
                  <span>Subtotal</span>
                  <span>₹{cartTotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>Shipping</span>
                  <span className="text-teal-400">FREE</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>Tax</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-4 mb-6">
                <div className="flex justify-between items-baseline">
                  <span className="font-heading text-xl font-bold uppercase">Total</span>
                  <span 
                    data-testid="cart-total"
                    className="font-heading text-3xl font-black"
                  >
                    ₹{cartTotal.toLocaleString()}
                  </span>
                </div>
              </div>

              <Link to="/checkout">
                <Button 
                  data-testid="checkout-btn"
                  className="w-full h-14 bg-teal-400 text-black hover:bg-white font-heading font-black uppercase tracking-widest skew-x-[-10deg] text-lg group"
                >
                  <span className="skew-x-[10deg] flex items-center justify-center gap-2">
                    Checkout
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Button>
              </Link>

              <Link to="/products">
                <Button 
                  variant="outline"
                  className="w-full h-12 mt-4 border-white/20 hover:border-teal-400 text-white hover:text-teal-400 font-heading font-bold uppercase tracking-widest bg-transparent"
                >
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
